import ReportCollectInputsButton from "@/components/ReportCollectInputsButton";
import type {
  CollectInputsPresentationProps,
  CollectInputsPreset,
} from "@/components/OfferPlacement.collect-inputs";
import { SacredCard } from "@/components/ui/SacredPrimitives";
import {
  ISHVARAM_COMMERCE_SYSTEM,
  ISHVARAM_REPORT_CHECKOUT_CHANNEL,
} from "@/lib/commerce-attribution";
import { buildReportInputHref, type ReportInputBirthContext } from "@/lib/report-input-href";
import type { SEOReportPlanId } from "@/lib/seo-intents";

/** The boxed report card presentation of OfferPlacement `mode="collect_inputs"`. */
export function CollectInputsCard(props: CollectInputsPresentationProps) {
  const Wrapper = props.wrapper ?? "div";
  return (
    <Wrapper className={props.className ?? "mx-auto my-8 max-w-md px-4 sm:px-6"}>
      {props.impression}
      {props.gated(<CollectInputsCardBody {...props} />)}
    </Wrapper>
  );
}

/**
 * A calculator's report card: bridge-only since 2026-08-12 (inline direct
 * checkout measured zero engagement on calculator pages), carrying the birth
 * details the seeker already entered so the form only asks them to confirm.
 */
export function calculatorReportOffer({
  planId,
  sourcePath,
  placement,
  customerProblem,
  language = "en",
  birth = {},
}: {
  planId: SEOReportPlanId;
  sourcePath: string;
  placement: string;
  customerProblem: string;
  language?: "en" | "hi";
  birth?: ReportInputBirthContext;
}): CollectInputsPreset {
  return {
    mode: "collect_inputs",
    presentation: CollectInputsCard,
    gate: true,
    placement,
    planId,
    intent: "chart_report",
    sourcePath,
    language,
    href: buildReportInputHref({
      sourcePath,
      intent: "chart_report",
      planId,
      language,
      birthContext: birth,
    }),
    ctaLabel: language === "hi" ? "जन्म विवरण भरें" : "Enter birth details",
    note: calculatorNote(language, Boolean(birth.birthDate)),
    customerProblem,
    urgency: "medium",
    confidence: 0.8,
    routeRankScore: 90,
    routeRankReason: `${customerProblem}_lookup_${planId}`,
    rankedOfferOrder: [planId],
  };
}

/**
 * The bridge link carries `open_checkout=1` (buildReportInputHref), so a result
 * with a birth time takes the seeker straight to this plan's checkout. The note
 * says so, and names the birth time, without which the result asks for it first.
 */
function calculatorNote(language: "en" | "hi", carriesDob: boolean): string {
  if (carriesDob) {
    return language === "hi"
      ? "आपके भरे हुए जन्म विवरण आगे साथ जाएँगे। जन्म समय सहित उनकी पुष्टि करें, कुंडली बनते ही आप सीधे रिपोर्ट के चेकआउट पर पहुँचेंगे।"
      : "The birth details you entered carry over. Confirm them with your birth time, and once your kundali is ready you go straight to the report's checkout.";
  }
  return language === "hi"
    ? "पहले जन्म समय सहित जन्म विवरण भरें। कुंडली बनते ही आप सीधे रिपोर्ट के चेकआउट पर पहुँचेंगे।"
    : "Enter your birth details with your birth time. Once your kundali is ready, you go straight to the report's checkout.";
}

function CollectInputsCardBody(props: CollectInputsPresentationProps) {
  const { planId, language, headline } = props;
  const Heading = props.headingLevel ?? "h3";
  const analyticsContext = props.analyticsContext ?? {
    checkoutChannel: ISHVARAM_REPORT_CHECKOUT_CHANNEL,
    commerceSystem: ISHVARAM_COMMERCE_SYSTEM,
    intent: props.intent,
    sourcePath: props.sourcePath,
    placement: props.placement,
    customerProblem: props.customerProblem,
    urgency: props.urgency,
    confidence: props.confidence,
    routeRankScore: props.routeRankScore,
    routeRankReason: props.routeRankReason,
    rankedOfferOrder: props.rankedOfferOrder,
  };
  return (
    <SacredCard
      className="p-5 sm:p-6 text-center"
      style={{
        background: "linear-gradient(135deg, var(--color-surface-warm, #fff8e1) 0%, #fff 100%)",
        borderColor: "var(--color-accent-border)",
      }}
    >
      <p
        className="text-[11px] font-bold uppercase tracking-wider mb-2"
        style={{ color: "var(--intent-brand-text)", letterSpacing: "0.12em" }}
      >
        {language === "hi" ? "प्रीमियम रिपोर्ट" : "Premium Report"}
      </p>
      <Heading
        className="text-[18px] sm:text-[20px] font-bold mb-2"
        style={{ color: "var(--ish-dark)", fontFamily: "PT Serif, Georgia, serif" }}
      >
        {headline}
      </Heading>
      <p className="text-body-small mb-4" style={{ color: "var(--ish-text-muted)" }}>
        {props.subhead}
      </p>
      <ReportTrustBlock language={language} headline={headline} reportNoun={props.reportNoun} />
      <ReportCollectInputsButton
        planId={planId}
        href={props.href}
        label={props.ctaLabel ?? collectInputsButtonLabel(language, props.priceInr)}
        analyticsContext={analyticsContext}
      />
      <p className="mt-3 text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
        {props.note ?? footerCopy(language)}
      </p>
    </SacredCard>
  );
}

function ReportTrustBlock({
  language,
  headline,
  reportNoun: reportNounOverride,
}: {
  language: "en" | "hi";
  headline: string;
  reportNoun?: string;
}) {
  const reportNoun = reportNounOverride ?? reportNounFromHeadline(headline, language);
  const bullets =
    language === "hi"
      ? [
          reportNounOverride ? `आपकी पूरी ${reportNoun}` : "आपकी पूरी रिपोर्ट",
          "तुरंत PDF और इसी पेज पर पूरी रिपोर्ट",
          "सुरक्षित Razorpay चेकआउट",
        ]
      : [
          `Your full ${reportNoun} reading`,
          "Instant PDF + on-page reading",
          "Secure Razorpay checkout",
        ];
  const reassurance =
    language === "hi"
      ? "गणना लाहिड़ी अयनांश पर — वही पद्धति जो पारंपरिक ज्योतिषी अपनाते हैं।"
      : "Calculated with Swiss Ephemeris on the Lahiri ayanamsa - the same method a traditional Jyotishi follows.";
  return (
    <div className="mx-auto mb-4 max-w-sm text-left" style={{ pointerEvents: "none" }}>
      <ul className="space-y-1.5">
        {bullets.map((bullet) => (
          <li
            key={bullet}
            className="flex items-start gap-2 text-caption"
            style={{ color: "var(--ish-text-muted)" }}
          >
            <TrustCheckIcon />
            <span>{bullet}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] leading-snug" style={{ color: "var(--ish-text-faint)" }}>
        {reassurance}
      </p>
    </div>
  );
}

function TrustCheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--ish-accent, var(--color-accent))"
      strokeWidth="2.5"
      className="mt-0.5 shrink-0"
      aria-hidden
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function reportNounFromHeadline(headline: string, language: "en" | "hi"): string {
  const cleaned = headline
    .replace(/^(get|unlock|see|read|view|download|order)\s+/i, "")
    .replace(/^(your|the|my|aapki|apni|aapka)\s+/i, "")
    .replace(/[—–:].*$/, "")
    .trim();
  if (cleaned) return cleaned;
  return language === "hi" ? "Kundali" : "Kundali";
}

function collectInputsButtonLabel(language: "en" | "hi", priceInr: number): string {
  return language === "hi"
    ? `जन्म विवरण भरें - ₹${priceInr}`
    : `Enter birth details - ₹${priceInr}`;
}

function footerCopy(language: "en" | "hi"): string {
  return language === "hi"
    ? "पहले जन्म विवरण भरें। कुंडली बनने के बाद उसके नीचे रिपोर्ट का भुगतान कर सकते हैं।"
    : "Enter birth details first. Your chart comes next, with the report's payment step below it.";
}
